"use client"

import { ColumnDef } from "@tanstack/react-table"
import { formatRelativeTime } from "~/common/format-relative-time"
import { parseISO } from "date-fns"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { gql } from "~/__generated__"
import { Switch } from "~/ui/switch"

const sourceUpdateMutation = gql(/* GraphQL */ `
  mutation SourceUpdate($id: ID!, $active: Boolean!) {
    sourceUpdate(input: { id: $id, active: $active }) {
      source {
        id
        active
      }
    }
  }
`)

export type SourceColumns = {
  id: string
  email: string
  url?: string | null
  lastNewsletterAt?: string | null
  active: boolean
}

function ActiveToggle({ id, active }: { id: string; active: boolean }) {
  const [sourceUpdate] = useSafeMutation(sourceUpdateMutation)

  const handleToggle = async () => {
    await sourceUpdate({
      variables: {
        id,
        active: !active,
      },
    })
  }

  return <Switch checked={active} onCheckedChange={handleToggle} />
}

export const columns: ColumnDef<SourceColumns | null | undefined>[] = [
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => {
      return <div className="text-sm">{row.getValue("email")}</div>
    },
  },
  {
    accessorKey: "url",
    header: "URL",
    cell: ({ row }) => {
      const url = row.getValue("url") as string | null | undefined
      if (!url) {
        return <div className="text-sm text-gray-500">-</div>
      }
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          {url}
        </a>
      )
    },
  },
  {
    accessorKey: "lastNewsletterAt",
    header: "Last Newsletter",
    cell: ({ row }) => {
      const lastNewsletterAt = row.getValue("lastNewsletterAt") as string | null | undefined
      if (!lastNewsletterAt) {
        return <div className="text-sm">Never</div>
      }
      return <div className="text-sm">{formatRelativeTime(parseISO(lastNewsletterAt))}</div>
    },
  },
  {
    accessorKey: "newsletterInputsCount",
    header: "Newsletters",
    cell: ({ row }) => {
      return <div className="text-right text-sm">{row.getValue("newsletterInputsCount")}</div>
    },
  },
  {
    accessorKey: "active",
    header: "Include",
    cell: ({ row }) => {
      return <ActiveToggle id={row.original?.id ?? ""} active={row.getValue("active") as boolean} />
    },
  },
]
